import React, { useState, useEffect } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import graphTheme from '../../graphTheme';
import { Spinner, SpinnerSize } from "@fluentui/react";

export enum GraphicType
{
    moneyGraphic = 1,
    linearGraphic = 2
}

export interface CustomRangeButton
{
    type: 'month' | 'year' | 'ytd' | 'all';
    count?: number;
    text: string;
    minDataPoints?: number;
}

type Props =
{
    series: any,
    customOptions: object | null,
    onChange?: (event: any, value?: any) => any;
    graphicType: GraphicType;
    customButtons?: CustomRangeButton[];
}

const HighStock: React.FC<Props> = ({series, customOptions, onChange, graphicType, customButtons}) =>
{
    // @ts-ignore
    Highcharts.setOptions(graphTheme);

    const calculateAllButtonIndex = () =>
    {
        // Si no hay botones personalizados, usar índice tradicional (5 = botón "Todo")
        if(!customButtons || customButtons.length === 0)
        {
            return 5;
        }
        
        // Si hay botones personalizados, calcular dinámicamente
        let buttonCount = 3; // Botones base temporales: 1m, 3m, 6m
        
        // Agregar botones personalizados que se renderizarán
        customButtons.forEach(btn =>
        {
            if((btn.type === 'month' || btn.type === 'year') && (!btn.minDataPoints || (series && Array.isArray(series) && series.length > 0)))
            {
                buttonCount++;
            }
        });
        
        // Verificar si necesitamos agregar 1y (si no está en customButtons)
        const has1y = customButtons.some(btn => btn.text === '1y');
        if(!has1y)
        {
            buttonCount++; // +1 para 1y
        }
        
        // +1 para YTD, +1 para Todo
        buttonCount += 2;
        
        // El índice del botón "Todo" es buttonCount - 1
        return buttonCount - 1;
    };
    
    const [buttonStatePressed, setStateButton] = React.useState<number>(calculateAllButtonIndex());

    // Función para calcular el número de puntos de datos en la serie más larga
    const getMaxDataPoints = React.useMemo(() =>
    {
        if(!series || !Array.isArray(series) || series.length === 0)
        {
            return 0;
        }
        
        let maxPoints = 0;
        series.forEach((s: any) =>
        {
            if(s.data && Array.isArray(s.data))
            {
                maxPoints = Math.max(maxPoints, s.data.length);
            }
        });
        
        return maxPoints;
    }, [series]);

    const buttonTextToIndex = React.useMemo(() =>
    {
        const mapping: {[key: string]: number} = {};
        
        // Si no hay botones personalizados, usar el mapeo tradicional
        if(!customButtons || customButtons.length === 0)
        {
            mapping['1m'] = 0;
            mapping['3m'] = 1;
            mapping['6m'] = 2;
            mapping['1y'] = 3;
            mapping['YTD'] = 4;
            mapping['Todo'] = 5;
            return mapping;
        }
        
        // Si hay botones personalizados, calcular el orden dinámicamente
        let index = 0;
 
        const baseTimeButtons =
        [
            { type: 'month', count: 1, text: '1m' },
            { type: 'month', count: 3, text: '3m' },
            { type: 'month', count: 6, text: '6m' }
        ];
        
        // Agregar botones personalizados temporales
        const customTimeButtons: any[] = [];
        customButtons.forEach(btn =>
        {
            if(btn.type === 'month' || btn.type === 'year')
            {
                // Si no tiene minDataPoints definido, agregarlo siempre
                if(!btn.minDataPoints)
                {
                    customTimeButtons.push(btn);
                }
                else
                {
                    // Si tiene minDataPoints, verificar si hay MÁS datos del requerido
                    if(getMaxDataPoints > btn.minDataPoints)
                    {
                        customTimeButtons.push(btn);
                    }
                }
            }
        });
        
        // Combinar y ordenar todos los botones temporales
        const allTimeButtons = [...baseTimeButtons, ...customTimeButtons];
        
        // Verificar si necesitamos agregar 1y
        const has1y = allTimeButtons.some(btn => btn.text === '1y');
        if(!has1y)
        {
            allTimeButtons.push({ type: 'year', count: 1, text: '1y' });
        }
        
        // Ordenar por duración
        allTimeButtons.sort((a: any, b: any) =>
        {
            const getOrder = (btn: any) =>
            {
                if(btn.type === 'month')
                {
                    return btn.count;
                }
                if(btn.type === 'year')
                {
                    return 12 * (btn.count || 1);
                }
                return 0;
            };
            return getOrder(a) - getOrder(b);
        });
        
        // Mapear índices
        allTimeButtons.forEach((btn: any) =>
        {
            mapping[btn.text] = index++;
        });
        
        // Agregar YTD y All al final
        mapping['YTD'] = index++;
        mapping['Todo'] = index++;
        
        return mapping;
    }, [customButtons, getMaxDataPoints]);

    const handleButton = React.useCallback((event: any) =>
    {
        if(event.currentTarget.textContent !== undefined && onChange !== undefined)
        {
            const pressed = event.currentTarget.textContent;
            const buttonIndex = buttonTextToIndex[pressed];

            if(buttonIndex !== undefined)
            {
                setStateButton(buttonIndex);
                onChange(buttonIndex);
            }
        }
    }, [buttonTextToIndex, onChange]);

    const rangeSelectorButtons = React.useMemo(() =>
    {
        // Si no hay botones personalizados, usar la configuración tradicional
        if(!customButtons || customButtons.length === 0)
        {
            return[
                {
                    type: 'month',
                    count: 1,
                    text: '1m',
                    events: { click: handleButton }
                },
                {
                    type: 'month',
                    count: 3,
                    text: '3m',
                    events: { click: handleButton }
                },
                {
                    type: 'month',
                    count: 6,
                    text: '6m',
                    events: { click: handleButton }
                },
                {
                    type: 'year',
                    count: 1,
                    text: '1y',
                    events: { click: handleButton }
                },
                {
                    type: 'ytd',
                    text: 'YTD',
                    events: { click: handleButton }
                },
                {
                    type: 'all',
                    text: 'Todo',
                    events: { click: handleButton }
                }
            ];
        }
        
        // Si hay botones personalizados, calcular el orden dinámicamente
        const timeButtons =
        [
            {
                type: 'month',
                count: 1,
                text: '1m',
                events: { click: handleButton }
            },
            {
                type: 'month',
                count: 3,
                text: '3m',
                events: { click: handleButton }
            },
            {
                type: 'month',
                count: 6,
                text: '6m',
                events: { click: handleButton }
            }
        ];

        // Agregamos los botones personalizados temporales (excluyendo YTD y All)
        customButtons.forEach(customBtn =>
        {
            // Filtrar por tipo (solo agregar month y year aquí)
            if(customBtn.type === 'month' || customBtn.type === 'year')
            {
                // Si no tiene minDataPoints definido, mostrarlo siempre
                if(!customBtn.minDataPoints)
                {
                    timeButtons.push(
                    {
                        type: customBtn.type,
                        count: customBtn.count,
                        text: customBtn.text,
                        events: { click: handleButton }
                    } as any);
                }
                else
                {
                    // Si tiene minDataPoints, verificar si hay suficientes datos
                    if(getMaxDataPoints > customBtn.minDataPoints)
                    {
                        timeButtons.push(
                        {
                            type: customBtn.type,
                            count: customBtn.count,
                            text: customBtn.text,
                            events: { click: handleButton }
                        } as any);
                    }
                }
            }
        });

        // Agregar 1y si no se agregó como botón personalizado
        const has1y = timeButtons.some(btn => btn.text === '1y');
        if(!has1y)
        {
            timeButtons.push(
            {
                type: 'year',
                count: 1,
                text: '1y',
                events: { click: handleButton }
            } as any);
        }

        timeButtons.sort((a: any, b: any) =>
        {
            const getOrder = (btn: any) =>
            {
                if(btn.type === 'month')
                {
                    return btn.count;
                }
                if(btn.type === 'year')
                {
                    return 12 * (btn.count || 1);
                }
                return 0;
            };
            return getOrder(a) - getOrder(b);
        });

        // Agregar YTD y All al final
        timeButtons.push(
        {
            type: 'ytd',
            text: 'YTD',
            events: { click: handleButton }
        } as any);

        timeButtons.push(
        {
            type: 'all',
            text: 'Todo',
            events: { click: handleButton }
        } as any);

        return timeButtons;
    }, [customButtons, getMaxDataPoints, handleButton]);

    const options = 
    {
        title:
        {
            text: ''
        }, // @ts-ignore
        series: series,
        plotOptions:
        {
            series:
            {
                compare: 'percent',
                showInNavigator: true
            }
        },
        rangeSelector:
        {
            allButtonsEnabled: true,
            selected: rangeSelectorButtons.length - 1, // Seleccionar el último botón ("Todo") por defecto
            buttons: rangeSelectorButtons
        },
        credits:
        {
            enabled: false,
        },
        yAxis:
        {
            labels:
            {
                formatter: function (item: any)
                {
                    return (item.value > 0 ? ' + ' : '') + item.value + '%';
                }
            },
            resize:
            {
                enabled: true
            },
            title:
            {
                text: ''
            }
        },
        tooltip:
        {
            // @ts-ignore
            pointFormatter: function ()
            {
                // @ts-ignore
                return this.series.name + ': ' + (this.y ? Highcharts.numberFormat(this.y, 4, ',', '.') : "") + ' (' + Highcharts.numberFormat(this.change, 2, ',', '.') + '%)';
            },
            valueSuffix: '',
            split: true
        },
        chart:
        {
            type: 'line',
            backgroundColor: 'none',
            height: 550,
        }
    };

    const [chartOptions, setChartOptions] = useState({...graphTheme, ...options, ...customOptions});

    useEffect(() =>
    {
        setChartOptions(prevState =>
        {
            return {...prevState, series: series}
        });
    }, [series]);

    useEffect(() =>
    {
        setChartOptions(prevState =>
        {
            if(buttonStatePressed !== -1)
            {
                prevState.rangeSelector.selected = buttonStatePressed;
            }

            return {...prevState, ...customOptions};
        });
    }, [customOptions, buttonStatePressed]);

    return(
        <React.Fragment>
            {(series.length === undefined || series.length === 0) &&
            (
                <Spinner size={SpinnerSize.large} />
            )}
            {series.length > 0 &&
            (
                <HighchartsReact highcharts={Highcharts} constructorType={'stockChart'}
                    // @ts-ignore
                    options={chartOptions}
                />
            )}
        </React.Fragment>
    );
};

export default HighStock;